import { useEffect, useState } from "react";
import { apiClient } from "../../../api/apiClient";
import {
  appendKeepFileIds,
  appendKeepImageIds,
  getContentId,
  getInitialFileIds,
  getInitialImageList,
  makePreviewImage,
} from "./contentFormUtils";

const getFileId = (file = {}) => {
  return (
    file.noticeFileId ||
    file.activityNoticeFileId ||
    file.activityFileId ||
    file.fileId ||
    file.attachmentId ||
    file.id ||
    null
  );
};

const getFileName = (file = {}) => {
  return (
    file.fileName ||
    file.originalFileName ||
    file.name ||
    file.originalName ||
    file.uploadFileName ||
    "기존 파일"
  );
};

const getInitialFileList = (data = {}) => {
  const result = [];

  const possibleFileArrays = [
    data.files,
    data.fileList,
    data.noticeFiles,
    data.activityNoticeFiles,
    data.attachments,
    data.attachmentList,
  ];

  possibleFileArrays.forEach((fileArray) => {
    if (!Array.isArray(fileArray)) return;

    fileArray.forEach((file) => {
      result.push({
        fileId: getFileId(file),
        fileName: getFileName(file),
        isExisting: true,
      });
    });
  });

  const possibleSingleFiles = [
    data.file,
    data.noticeFile,
    data.activityNoticeFile,
    data.attachment,
  ];

  possibleSingleFiles.forEach((file) => {
    if (!file || typeof file !== "object") return;

    result.push({
      fileId: getFileId(file),
      fileName: getFileName(file),
      isExisting: true,
    });
  });

  if (result.length === 0 && data?.fileName) {
    result.push({
      fileId:
        data?.noticeFileId ||
        data?.activityNoticeFileId ||
        data?.activityFileId ||
        data?.fileId ||
        data?.attachmentId ||
        data?.id ||
        null,
      fileName: data.fileName,
      isExisting: true,
    });
  }

  return result.filter((file, index, array) => {
    const key = file.fileId || file.fileName;

    return (
      array.findIndex((target) => {
        const targetKey = target.fileId || target.fileName;
        return String(targetKey) === String(key);
      }) === index
    );
  });
};

const getKeepFileIdsFromFiles = (fileList = [], initialData = {}) => {
  const idsFromFileList = fileList.map((file) => file.fileId).filter(Boolean);

  const idsFromUtil = getInitialFileIds(initialData) || [];

  return [...new Set([...idsFromFileList, ...idsFromUtil])];
};

function NoticeForm({
  mode = "write",
  initialData = {},
  onCancel,
  onSaveComplete,
}) {
  const isEdit = mode === "edit";

  const initialFileList = getInitialFileList(initialData);

  const [title, setTitle] = useState(initialData?.title || "");
  const [content, setContent] = useState(initialData?.content || "");

  const [existingFiles, setExistingFiles] = useState(initialFileList);
  const [files, setFiles] = useState([]);

  const [images, setImages] = useState(() => getInitialImageList(initialData));
  const [imageFiles, setImageFiles] = useState([]);

  const [keepFileIds, setKeepFileIds] = useState(() =>
    getKeepFileIdsFromFiles(initialFileList, initialData)
  );

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isEdit) return;

    const nextExistingFiles = getInitialFileList(initialData);
    const nextKeepFileIds = getKeepFileIdsFromFiles(
      nextExistingFiles,
      initialData
    );

    setTitle(initialData?.title || "");
    setContent(initialData?.content || "");

    setExistingFiles(nextExistingFiles);
    setFiles([]);

    setImages(getInitialImageList(initialData));
    setImageFiles([]);

    setKeepFileIds(nextKeepFileIds);

    console.log("공지사항 수정 initialData:", initialData);
    console.log("공지사항 기존 파일 목록:", nextExistingFiles);
    console.log("공지사항 기존 파일 유지 ID:", nextKeepFileIds);
  }, [isEdit, initialData]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);

    if (!selectedFiles.length) return;

    setFiles((prevFiles) => {
      const mergedFiles = [...prevFiles];

      selectedFiles.forEach((selectedFile) => {
        const isDuplicate = mergedFiles.some(
          (file) =>
            file.name === selectedFile.name &&
            file.size === selectedFile.size &&
            file.lastModified === selectedFile.lastModified
        );

        if (!isDuplicate) {
          mergedFiles.push(selectedFile);
        }
      });

      return mergedFiles;
    });

    e.target.value = "";
  };

  const handleRemoveExistingFile = (index) => {
    const target = existingFiles[index];

    setExistingFiles((prev) => prev.filter((_, i) => i !== index));

    if (target?.fileId) {
      setKeepFileIds((prev) =>
        prev.filter((fileId) => String(fileId) !== String(target.fileId))
      );
    }
  };

  const handleRemoveNewFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleImageChange = (e) => {
    const selectedFiles = Array.from(e.target.files || []);

    if (!selectedFiles.length) return;

    const remainCount = 5 - images.length;
    const limitedFiles = selectedFiles.slice(0, Math.max(remainCount, 0));

    if (!limitedFiles.length) {
      alert("이미지는 최대 5개까지 등록할 수 있습니다.");
      e.target.value = "";
      return;
    }

    const newImages = limitedFiles.map((selectedFile) =>
      makePreviewImage(selectedFile, false)
    );

    setImages((prev) => [...prev, ...newImages].slice(0, 5));
    setImageFiles((prev) => [...prev, ...limitedFiles].slice(0, 5));

    e.target.value = "";
  };

  const handleRemoveImage = (index) => {
    const target = images[index];

    const newImageIndex = images
      .slice(0, index)
      .filter((image) => !image.isExisting && !image.isMain).length;

    setImages((prev) => prev.filter((_, i) => i !== index));

    if (!target?.isMain && !target?.isExisting) {
      setImageFiles((prev) => prev.filter((_, i) => i !== newImageIndex));
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      alert("제목을 입력하세요.");
      return;
    }

    if (!content.trim()) {
      alert("글 내용을 입력하세요.");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();

      formData.append("title", title);
      formData.append("content", content);

      if (isEdit) {
        appendKeepFileIds(formData, keepFileIds);
        appendKeepImageIds(formData, images);
      }

      files.forEach((file) => {
        formData.append("files", file);
      });

      imageFiles.forEach((imageFile) => {
        formData.append("imageFiles", imageFile);
      });

      console.log("공지사항 저장 keepFileIds:", keepFileIds);
      console.log("공지사항 저장 files:", files);

      for (const pair of formData.entries()) {
        console.log("Notice FormData:", pair[0], pair[1]);
      }

      if (isEdit) {
        await apiClient(`/api/activity/notice/${getContentId(initialData)}`, {
          method: "PUT",
          body: formData,
        });
      } else {
        await apiClient("/api/activity/notice", {
          method: "POST",
          body: formData,
        });
      }

      alert(isEdit ? "수정되었습니다." : "등록되었습니다.");

      if (onSaveComplete) {
        onSaveComplete();
      }
    } catch (error) {
      console.error("공지사항 저장 오류:", error);
      alert(error.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const hasFiles = existingFiles.length > 0 || files.length > 0;

  return (
    <div className="content-form-page notice-form-page">
      <h2 className="content-form-title">
        {isEdit ? "공지사항 수정" : "새 공지사항 작성"}
      </h2>

      <div className="content-form notice-form">
        <div className="content-form-row">
          <label className="content-label">제목</label>

          <input
            className="content-input"
            type="text"
            placeholder="제목을 입력해 주세요."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="content-form-row content-form-row-top">
          <label className="content-label">파일 첨부</label>

          <div className="content-file-area">
            <label className="content-file-btn">
              내 PC
              <input type="file" hidden multiple onChange={handleFileChange} />
            </label>

            <div className="content-file-drop">
              {hasFiles ? (
                <div className="content-file-list">
                  {existingFiles.map((existingFile, index) => (
                    <div
                      className="content-file-item"
                      key={`existing-${existingFile.fileId || index}`}
                    >
                      <button
                        type="button"
                        className="file-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveExistingFile(index);
                        }}
                      >
                        ×
                      </button>

                      <span>{existingFile.fileName}</span>
                    </div>
                  ))}

                  {files.map((file, index) => (
                    <div
                      className="content-file-item"
                      key={`new-${file.name}-${file.size}-${file.lastModified}`}
                    >
                      <button
                        type="button"
                        className="file-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveNewFile(index);
                        }}
                      >
                        ×
                      </button>

                      <span>{file.name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="content-file-placeholder">
                  파일을 첨부하려면 드래그하세요.
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="content-form-row content-form-row-top">
          <label className="content-label">글쓰기</label>

          <textarea
            className="content-textarea notice-textarea"
            placeholder={"글을 작성해 주세요.\n(최대 5,000자까지 입력 가능합니다.)"}
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
        </div>

        <div className="content-form-row content-form-row-top">
          <label className="content-label">이미지</label>

          <div className="content-image-box">
            {images.map((img, index) => (
              <div
                className="content-image-item"
                key={`${img.imageId || img.fileName || "image"}-${index}`}
              >
                <button
                  type="button"
                  className="content-image-remove"
                  onClick={() => handleRemoveImage(index)}
                >
                  ×
                </button>

                {img.preview || img.url ? (
                  <img src={img.preview || img.url} alt="preview" />
                ) : (
                  <span>{img.fileName || "기존 이미지"}</span>
                )}
              </div>
            ))}

            {images.length < 5 && (
              <label className="content-image-add">
                +
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  multiple
                  onChange={handleImageChange}
                />
              </label>
            )}
          </div>
        </div>

        <div className="content-form-footer">
          <button
            type="button"
            className="content-cancel-btn"
            onClick={onCancel}
            disabled={loading}
          >
            취소
          </button>

          <button
            type="button"
            className="content-save-btn"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "저장 중..." : "저장"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NoticeForm;